export type UnitStatus = 'leased' | 'transient' | 'expiring' | 'vacant' | 'maintenance' | 'underpriced' | 'occupied_no_contract'

export interface UnitData {
  id: string
  unit_code: string
  building_id: string
  building_name: string
  unit_type: 'Hangar' | 'Tiedown' | 'Ramp' | 'Community' | 'Office'
  sqft: number
  door_width?: number
  door_height?: number
  status: UnitStatus
  tenant_name?: string
  aircraft_tail?: string
  aircraft_type?: string
  monthly_rate?: number
  contract_end_date?: string
  days_to_expiry?: number
  renewal_status?: 'not_started' | 'in_progress' | 'signed'
}

export interface BuildingData {
  id: string
  name: string
  total_units: number
  total_sqft: number
  occupied_sqft: number
  occupancy_percent: number
  contracted_revenue: number
  avg_rate_per_sqft: number
  units: UnitData[]
}

export interface KpiData {
  contracted_monthly_revenue: number
  contracted_annualized: number
  occupancy_percent_sqft: number
  occupancy_sqft_occupied: number
  occupancy_sqft_total: number
  occupancy_percent_units: number
  occupancy_units_occupied: number
  occupancy_units_total: number
  vacant_potential_monthly: number
  vacant_potential_annualized: number
  vacant_units_count: number
  expiring_count_90_days: number
  expiring_revenue_risk: number
}

// Mock KPI Data
export const mockKpiData: KpiData = {
  contracted_monthly_revenue: 47200,
  contracted_annualized: 566400,
  occupancy_percent_sqft: 82,
  occupancy_sqft_occupied: 45200,
  occupancy_sqft_total: 58000,
  occupancy_percent_units: 75,
  occupancy_units_occupied: 15,
  occupancy_units_total: 20,
  vacant_potential_monthly: 12400,
  vacant_potential_annualized: 148800,
  vacant_units_count: 5,
  expiring_count_90_days: 4,
  expiring_revenue_risk: 15600,
}

// Mock Units for Hangar A (6 units)
const hangarAUnits: UnitData[] = [
  {
    id: 'u-a1',
    unit_code: 'A-1',
    building_id: 'b-a',
    building_name: 'Hangar A',
    unit_type: 'Hangar',
    sqft: 2400,
    door_width: 60,
    door_height: 18,
    status: 'leased',
    tenant_name: 'ABC Corp',
    aircraft_tail: 'N123AB',
    aircraft_type: 'G650',
    monthly_rate: 2640,
    contract_end_date: '2026-12-31',
  },
  {
    id: 'u-a2',
    unit_code: 'A-2',
    building_id: 'b-a',
    building_name: 'Hangar A',
    unit_type: 'Hangar',
    sqft: 2200,
    door_width: 55,
    door_height: 16,
    status: 'leased',
    tenant_name: 'XYZ LLC',
    aircraft_tail: 'N456CD',
    aircraft_type: 'PC-12',
    monthly_rate: 2420,
    contract_end_date: '2027-03-15',
  },
  {
    id: 'u-a3',
    unit_code: 'A-3',
    building_id: 'b-a',
    building_name: 'Hangar A',
    unit_type: 'Hangar',
    sqft: 2000,
    door_width: 50,
    door_height: 16,
    status: 'underpriced',
    tenant_name: 'Smith',
    aircraft_tail: 'N789EF',
    aircraft_type: 'CJ3',
    monthly_rate: 1700,
    contract_end_date: '2026-06-30',
  },
  {
    id: 'u-a4',
    unit_code: 'A-4',
    building_id: 'b-a',
    building_name: 'Hangar A',
    unit_type: 'Hangar',
    sqft: 2400,
    door_width: 60,
    door_height: 18,
    status: 'vacant',
  },
  {
    id: 'u-a5',
    unit_code: 'A-5',
    building_id: 'b-a',
    building_name: 'Hangar A',
    unit_type: 'Hangar',
    sqft: 1500,
    door_width: 45,
    door_height: 14,
    status: 'leased',
    tenant_name: 'Jet Mgmt',
    aircraft_tail: 'N321GH',
    aircraft_type: 'Phenom 300',
    monthly_rate: 1575,
    contract_end_date: '2026-09-01',
  },
  {
    id: 'u-a6',
    unit_code: 'A-6',
    building_id: 'b-a',
    building_name: 'Hangar A',
    unit_type: 'Hangar',
    sqft: 1500,
    door_width: 45,
    door_height: 14,
    status: 'leased',
    tenant_name: 'Prime',
    aircraft_tail: 'N654IJ',
    aircraft_type: 'C525',
    monthly_rate: 1650,
    contract_end_date: '2027-01-01',
  },
]

// Mock Units for Hangar B (3 units)
const hangarBUnits: UnitData[] = [
  {
    id: 'u-b1',
    unit_code: 'B-1',
    building_id: 'b-b',
    building_name: 'Hangar B',
    unit_type: 'Hangar',
    sqft: 3000,
    status: 'leased',
    tenant_name: 'Global Inc',
    aircraft_tail: 'N111KL',
    aircraft_type: 'G550',
    monthly_rate: 3450,
    contract_end_date: '2026-08-15',
  },
  {
    id: 'u-b2',
    unit_code: 'B-2',
    building_id: 'b-b',
    building_name: 'Hangar B',
    unit_type: 'Hangar',
    sqft: 2500,
    status: 'expiring',
    tenant_name: 'Charter Co',
    aircraft_tail: 'N222MN',
    aircraft_type: 'Falcon 2000',
    monthly_rate: 2700,
    contract_end_date: '2026-04-10', // < 90 days from "today" (Feb 2026)
    days_to_expiry: 52,
  },
  {
    id: 'u-b3',
    unit_code: 'B-3',
    building_id: 'b-b',
    building_name: 'Hangar B',
    unit_type: 'Hangar',
    sqft: 2500,
    status: 'vacant',
  },
]

// Mock Units for Tiedowns (12 units - simplifying for brevity but showing variety)
const tiedownUnits: UnitData[] = Array.from({ length: 12 }, (_, i) => {
    const num = i + 1;
    const isEven = num % 2 === 0;
    const isMobjs = num % 3 === 0;
    
    if (num === 11) {
        return {
            id: `u-t${num}`,
            unit_code: `T-${num}`,
            building_id: 'b-t',
            building_name: 'Tiedowns',
            unit_type: 'Tiedown',
            sqft: 400,
            status: 'transient',
            tenant_name: 'Transient Guest',
            aircraft_tail: `N999${num}`,
            aircraft_type: 'C172',
        } as UnitData
    }

    if (isMobjs && !isEven) {
         return {
            id: `u-t${num}`,
            unit_code: `T-${num}`,
            building_id: 'b-t',
            building_name: 'Tiedowns',
            unit_type: 'Tiedown',
            sqft: 400,
            status: 'vacant',
        } as UnitData
    }

    return {
        id: `u-t${num}`,
        unit_code: `T-${num}`,
        building_id: 'b-t',
        building_name: 'Tiedowns',
        unit_type: 'Tiedown',
        sqft: 400,
        status: 'leased',
        tenant_name: `Owner ${num}`,
        aircraft_tail: `N88${num}`,
        aircraft_type: 'SR22',
        monthly_rate: 150,
        contract_end_date: '2026-12-31',
    } as UnitData
})


export const mockBuildings: BuildingData[] = [
  {
    id: 'b-a',
    name: 'Hangar A',
    total_units: 6,
    total_sqft: 12000,
    occupied_sqft: 9840,
    occupancy_percent: 82,
    contracted_revenue: 18200, // Approximate sum
    avg_rate_per_sqft: 1.04,
    units: hangarAUnits,
  },
  {
    id: 'b-b',
    name: 'Hangar B',
    total_units: 3,
    total_sqft: 8000,
    occupied_sqft: 5500,
    occupancy_percent: 69,
    contracted_revenue: 14100, // Approximate sum
    avg_rate_per_sqft: 1.12,
    units: hangarBUnits,
  },
   {
    id: 'b-t',
    name: 'Tiedowns',
    total_units: 12,
    total_sqft: 4800, // 12 * 400
    occupied_sqft: 3600, // approx
    occupancy_percent: 75,
    contracted_revenue: 1500, // approx
    avg_rate_per_sqft: 0.38,
    units: tiedownUnits,
  },
]

export const allMockUnits = [...hangarAUnits, ...hangarBUnits, ...tiedownUnits]
